import type { ProgramQuote } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import { formatLenderProgramDisplay } from '@makemydeal/dr-shared-ui-utils';

export type NoProgramForTermParts = {
    months?: number;
    offerType?: OfferType;
    programDisplay?: string;
    availableTerms?: number[];
};

export type ProgramLabelParts = {
    offerType: OfferType;
    allTiersMode: boolean;
    quotes: ProgramQuote[];
    months?: number;
    lenderLegalName?: string;
    programName?: string;
    retainedLenderLegalName?: string;
    retainedProgramName?: string;
};

export const hasQuoteForTerm = (quotes: ProgramQuote[], months?: number): boolean =>
    months != null && quotes.some((quote) => quote.term === months);

export const getProgramTerms = (quotes: ProgramQuote[], lenderLegalName?: string, programName?: string): number[] => {
    if (!programName) {
        return [];
    }
    const terms = quotes
        .filter((quote) => quote.lenderLegalName === lenderLegalName && quote.lenderProgram?.programName === programName)
        .map((quote) => quote.term)
        .filter((term): term is number => term != null);
    return [...new Set(terms)].sort((first, second) => first - second);
};

export const formatOfferTypeLabel = (offerType?: OfferType): string =>
    offerType ? `${offerType.charAt(0).toUpperCase()}${offerType.slice(1)}` : '';

export const buildNoProgramForTermMessage = ({
    months,
    offerType,
    programDisplay,
    availableTerms = []
}: NoProgramForTermParts): string => {
    const termPhrase = months == null ? 'the selected term' : `${months}mo`;
    const context = [programDisplay, formatOfferTypeLabel(offerType)].filter(Boolean).join(' - ');
    const lead = context ? `${context} - No program offered for ${termPhrase}` : `No program offered for ${termPhrase}`;
    return availableTerms.length > 0 ? `${lead}, available at ${availableTerms.join(', ')}mo` : lead;
};

export const resolveProgramLabel = ({
    offerType,
    allTiersMode,
    quotes,
    months,
    lenderLegalName,
    programName,
    retainedLenderLegalName,
    retainedProgramName
}: ProgramLabelParts): string => {
    if (offerType === CASH) {
        return 'Cash';
    }
    const termDisplay = formatLenderProgramDisplay(lenderLegalName, programName);
    if (!allTiersMode || quotes.length === 0 || hasQuoteForTerm(quotes, months)) {
        return termDisplay;
    }
    const paymentLenderLegalName = lenderLegalName || retainedLenderLegalName;
    const paymentProgramName = programName || retainedProgramName;
    return buildNoProgramForTermMessage({
        months,
        offerType,
        programDisplay: formatLenderProgramDisplay(paymentLenderLegalName, paymentProgramName),
        availableTerms: getProgramTerms(quotes, paymentLenderLegalName, paymentProgramName)
    });
};
